import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { EntraIdUserResponse, EntraSearchQuery } from '../../shared/interfaces/EntraIdUser';

@Injectable({
    providedIn: 'root'
})
export class GraphService {
    private msalService = inject(MsalService);
    private http = inject(HttpClient);

    /**
     * Get a specific user's profile photo by ID or UPN
     * @param userIdentifier User ID, UPN, or 'me' for current user
     * @param scopes the scopes required for the Graph API
     * @returns Observable with the photo as a Blob
     */
    public getPhotoForUser(userIdentifier: string, scopes: string[]): Observable<Blob> {
        return this.getGraphToken(scopes).pipe(
            switchMap(token => {
                const headers = new HttpHeaders({
                    Authorization: `Bearer ${token}`
                });

                return this.http.get(`${environment.apiConfig[0].uri}/users/${userIdentifier}/photo/$value`, {
                    headers,
                    responseType: 'blob'
                });
            }),
            catchError(error => {
                console.error('Error fetching user photo:', error);
                // Return a default photo or placeholder
                return throwError(() => new Error('Failed to fetch user photo'));
            })
        );
    }

    /**
     * Get a token specifically for Microsoft Graph API
     * @returns Observable with the access token
     */
    private getGraphToken(scopes: string[]): Observable<string> {
        const account = this.msalService.instance.getActiveAccount();
        if (!account) {
            return throwError(() => new Error('No active account! Please sign in before trying again.'));
        }

        const request = {
            scopes,
            account: account
        };

        return from(this.msalService.instance.acquireTokenSilent(request)).pipe(
            switchMap(response => of(response.accessToken)),
            catchError(error => {
                console.error('Silent token acquisition failed', error);
                // Fallback to interactive method if silent acquisition fails
                return from(this.msalService.instance.acquireTokenPopup(request)).pipe(
                    switchMap(response => of(response.accessToken)),
                    catchError(interactiveError => {
                        console.error('Interactive token acquisition failed', interactiveError);
                        return throwError(() => new Error('Failed to acquire token'));
                    })
                );
            })
        );
    }

    /**
     * Get user details by ID or UPN
     * @param userIdentifier User ID or UPN
     * @returns Observable with user details
     */
    public getUserDetails(userIdentifier: string): Observable<EntraIdUserResponse> {
        const scopes = environment.apiConfig[0].scopes;
        return this.getGraphToken(scopes).pipe(
            switchMap(token => {
                const headers = new HttpHeaders({
                    Authorization: `Bearer ${token}`
                });

                return this.http.get<EntraIdUserResponse>(`${environment.apiConfig[0].uri}/users/${userIdentifier}`, {
                    headers
                });
            })
        );
    }

    public searchUser(query: EntraSearchQuery): Observable<EntraIdUserResponse> {
        const scopes = environment.apiConfig[0].scopes;
        return this.getGraphToken(scopes).pipe(
            switchMap(token => {
                const headers = new HttpHeaders({
                    Authorization: `Bearer ${token}`
                });

                const searchConditions: string[] = [];
                if (query.firstName) {
                    searchConditions.push(`startswith(givenName,'${query.firstName}')`);
                }
                if (query.lastName) {
                    searchConditions.push(`startswith(surname,'${query.lastName}')`);
                }

                if (query.gid) {
                    searchConditions.push(`employeeId eq '${query.gid}'`);
                }
                const searchQuery = searchConditions.join(' and ');

                return this.http.get<EntraIdUserResponse>(
                    `${environment.apiConfig[0].uri}/users?$filter=${searchQuery}&$select=id,displayName,givenName,surname,mail,userPrincipalName,employeeId,department,companyName`,
                    {
                        headers
                    }
                );
            }),
            catchError(error => {
                console.error('Error searching for users:', error);
                return throwError(() => new Error('Failed to search for users'));
            })
        );
    }
}
